import type { ReactElement } from 'react';
import { ArrowLeft, ArrowRight, Download, Pencil, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { SeverityBadge } from '@/components/policylist/components/SeverityBadge';
import { BodyMuted } from '@/components/shared/typography/BodyText';
import {
  CatalogId,
  catalogFrameworkLabel,
  type CatalogCategory,
  type CatalogIdType,
} from '@kirin/api-client/contracts/ai-instruction-catalog';

interface CategoryDetailPanelProps {
  category: CatalogCategory;
  /** Which catalog this panel belongs to, so the framework label is correct. */
  catalog?: CatalogIdType;
  index: number;
  total: number;
  canEdit: boolean;
  onToggleEnabled: (enabled: boolean) => void;
  onEdit: () => void;
  onExport: () => void;
  onDelete?: () => void;
  onPrev: () => void;
  onNext: () => void;
  togglePending: boolean;
  deletePending: boolean;
}

/**
 * KIR-3515: right-hand detail panel for a selected catalog category (why it
 * matters, detection keywords, example instruction) with per-category
 * enable/disable, edit, export, delete (custom only), and prev/next nav.
 */
export function CategoryDetailPanel({
  category,
  catalog = CatalogId.SecureCoding,
  index,
  total,
  canEdit,
  onToggleEnabled,
  onEdit,
  onExport,
  onDelete,
  onPrev,
  onNext,
  togglePending,
  deletePending,
}: CategoryDetailPanelProps): ReactElement {
  const frameworkLabel = catalogFrameworkLabel(catalog, category.framework);

  return (
    <div className="space-y-4" data-testid="category-detail">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 space-y-2">
          <div className="text-xs font-mono text-muted-foreground">
            {String(index + 1).padStart(2, '0')}
          </div>
          <h3 className="text-lg font-semibold">{category.name}</h3>
          <div className="flex flex-wrap items-center gap-2">
            <SeverityBadge riskLevel={category.severity} showIcon />
            {category.reference && (
              <Badge variant="outline">
                {frameworkLabel} {category.reference}
              </Badge>
            )}
            {!category.enabled && <Badge variant="secondary">Disabled</Badge>}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Label
            htmlFor={`enable-${category.id}`}
            className="text-sm text-muted-foreground"
          >
            {category.enabled ? 'On' : 'Off'}
          </Label>
          <Switch
            id={`enable-${category.id}`}
            checked={category.enabled}
            disabled={!canEdit || togglePending}
            onCheckedChange={onToggleEnabled}
            aria-label={`Toggle ${category.name}`}
          />
        </div>
      </div>

      <section className="space-y-1">
        <BodyMuted className="text-xs font-semibold uppercase tracking-wide">
          Why this matters for AI agents
        </BodyMuted>
        <p className="text-sm">{category.description}</p>
      </section>

      <section className="space-y-1">
        <BodyMuted className="text-xs font-semibold uppercase tracking-wide">
          Detection keywords — scanner uses these to check instruction files
        </BodyMuted>
        <div className="flex flex-wrap gap-1.5">
          {category.keywords.map((kw, i) => (
            <Badge
              key={`${kw}-${i}`}
              variant="secondary"
              className="font-normal"
            >
              {kw}
            </Badge>
          ))}
        </div>
      </section>

      <section className="space-y-1">
        <BodyMuted className="text-xs font-semibold uppercase tracking-wide">
          Example instruction for CLAUDE.md / .cursorrules
        </BodyMuted>
        <blockquote className="rounded-md border bg-muted/40 p-3 text-sm italic">
          {category.instruction}
        </blockquote>
      </section>

      <div className="flex flex-wrap items-center gap-2 border-t pt-3">
        {canEdit && (
          <Button size="sm" onClick={onEdit}>
            <Pencil className="mr-1 size-4" />
            Edit
          </Button>
        )}
        <Button size="sm" variant="outline" onClick={onExport}>
          <Download className="mr-1 size-4" />
          Export JSON
        </Button>
        {canEdit && !category.isBuiltIn && onDelete && (
          <Button
            size="sm"
            variant="outline"
            onClick={onDelete}
            disabled={deletePending}
          >
            <Trash2 className="mr-1 size-4" />
            Delete
          </Button>
        )}
        <div className="ml-auto flex items-center gap-2">
          <Button
            size="sm"
            variant="ghost"
            onClick={onPrev}
            disabled={index <= 0}
          >
            <ArrowLeft className="mr-1 size-4" />
            Previous
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={onNext}
            disabled={index >= total - 1}
          >
            Next
            <ArrowRight className="ml-1 size-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
